// Zero-dependency, client-side catalog search.
//
// The whole catalog ships to the browser already (one fetchCatalog() call), so
// search runs entirely in memory — no per-keystroke network, identical in
// server and static builds. We precompute a lowercased searchable string per
// field once, then rank items by a weighted substring score per query.

// Field weights — name/SKU dominate, then taxonomy, then prose.
const W_NAME = 100;
const W_SKU = 90;
const W_CATEGORY = 40;
const W_SUBCATEGORY = 40;
const W_REST = 10; // description, features, materials, sizes
const BONUS_NAME_PREFIX = 50; // name starts with the term
const BONUS_NAME_EXACT = 200; // whole query equals the name

const lc = (v) => (typeof v === "string" ? v.toLowerCase() : "");
const joinList = (v) => (Array.isArray(v) ? v.join(" ") : typeof v === "string" ? v : "");

/** Flatten the grouped /catalog payload (array of {category, items}) to a flat item list. */
export function flattenCatalog(groups) {
  if (!Array.isArray(groups)) return [];
  const out = [];
  for (const g of groups) {
    if (Array.isArray(g?.items)) out.push(...g.items);
  }
  return out;
}

/** Precompute per-item lowercased fields. Pass the flat item list. */
export function buildIndex(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    item,
    name: lc(item.name),
    sku: lc(item.sku),
    category: lc(item.category),
    subCategory: lc(item.subCategory),
    rest: lc(
      [
        typeof item.description === "string" ? item.description : joinList(item.description),
        joinList(item.features),
        joinList(item.materials),
        item.sizes,
      ]
        .filter(Boolean)
        .join(" ")
    ),
  }));
}

// Best single-term score across an entry's fields. Returns 0 when the term
// appears nowhere (so callers can treat the term as unmatched).
function termScore(entry, term) {
  let best = 0;
  if (entry.name.includes(term)) {
    best = Math.max(best, W_NAME + (entry.name.startsWith(term) ? BONUS_NAME_PREFIX : 0));
  }
  if (entry.sku.includes(term)) best = Math.max(best, W_SKU);
  if (entry.category.includes(term)) best = Math.max(best, W_CATEGORY);
  if (entry.subCategory.includes(term)) best = Math.max(best, W_SUBCATEGORY);
  if (entry.rest.includes(term)) best = Math.max(best, W_REST);
  return best;
}

/**
 * Rank index entries against a query. All whitespace-separated terms must match
 * somewhere (AND). Returns matching `item`s, best-first, capped at `limit`
 * (0/undefined = no cap).
 */
export function searchCatalog(index, query, limit = 0) {
  const q = (query ?? "").trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/);

  const scored = [];
  for (const entry of index) {
    let total = 0;
    let matchedAll = true;
    for (const term of terms) {
      const s = termScore(entry, term);
      if (s === 0) {
        matchedAll = false;
        break;
      }
      total += s;
    }
    if (!matchedAll) continue;
    // Whole-query bonuses on the name make exact/prefix name hits float to top.
    if (entry.name === q) total += BONUS_NAME_EXACT;
    else if (entry.name.startsWith(q)) total += BONUS_NAME_PREFIX;
    scored.push({ item: entry.item, score: total, name: entry.name });
  }

  scored.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
  const items = scored.map((s) => s.item);
  return limit > 0 ? items.slice(0, limit) : items;
}
